import React from 'react';
import BaseScadaNode from './BaseScadaNode';

export function HopperScrewSVG({ config = {}, width = 60, height = 90, liveValues = {} }) {
    const threshold = parseFloat(config.threshold ?? 0.5);
    const stateVal = liveValues.state;
    const running = stateVal != null && parseFloat(stateVal) >= threshold;
    const color = running ? (config.run_color || '#22C55E') : (config.stop_color || '#64748B');

    // Continuous level (analog)
    const levelVal = liveValues.level;
    const levelBound = levelVal != null;
    const levelNum = levelBound ? Math.min(1, Math.max(0, parseFloat(levelVal ?? 0) / 100)) : null;

    const cx = width / 2;
    const hopperH = height * 0.55;
    const screwH = height * 0.32;
    const screwW = width * 0.3;
    const screwX = cx - screwW / 2;
    const screwY = hopperH;
    const outletH = height * 0.1;
    const outletY = screwY + screwH;

    // Hopper trapezoid dims
    const capCount = parseInt(config.level_cap_count ?? 0);
    // Leave right margin for capacitive sensor dots
    const sensorMargin = capCount > 0 ? 12 : 0;
    const topW = width - 8 - sensorMargin;
    const botW = screwW + 8;
    const topX = (width - topW - sensorMargin) / 2;
    const botX = (width - botW) / 2;

    // Fill polygon (analog level)
    let fillPoly = null;
    if (levelBound && levelNum > 0) {
        const fillH = hopperH * levelNum * 0.85;
        const fillY = hopperH - fillH;
        const fillTopW = topW * (fillY / hopperH) + botW * (1 - fillY / hopperH);
        const fillTopX = (width - fillTopW - sensorMargin) / 2;
        fillPoly = `${fillTopX},${fillY} ${fillTopX + fillTopW},${fillY} ${botX + botW},${hopperH} ${botX},${hopperH}`;
    }

    // Capacitive sensor dots (right side of hopper)
    const sensorDots = capCount > 0 ? Array.from({ length: capCount }, (_, i) => {
        const key = `level_cap_${i + 1}`;
        const val = liveValues[key];
        const active = val != null && (val === true || val === 1 || val === '1' || val === 'true');
        const unbound = val == null;
        // bottom sensor = index 0, top = index N-1
        const fraction = capCount === 1 ? 0.5 : i / (capCount - 1);
        const cy = hopperH - (hopperH * 0.85 * fraction + hopperH * 0.075);
        return { key, active, unbound, cy };
    }) : [];

    const hopperRightX = topX + topW; // rightmost edge of hopper at current row
    const sensorX = width - sensorMargin / 2 - 2;

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
            {/* Hopper fill (analog) */}
            {fillPoly && (
                <polygon points={fillPoly} fill="rgba(59,130,246,0.3)" />
            )}

            {/* Hopper outline */}
            <polygon
                points={`${topX},${2} ${topX + topW},${2} ${botX + botW},${hopperH} ${botX},${hopperH}`}
                fill="none" stroke={color} strokeWidth={2}
            />

            {/* Screw tube */}
            <rect x={screwX} y={screwY} width={screwW} height={screwH} rx={3} fill="#1E293B" stroke={color} strokeWidth={1.5} />
            {/* Helicoidal lines */}
            {[0, 1, 2, 3, 4].map(i => {
                const yPos = screwY + (screwH / 5) * i + screwH / 10;
                return <line key={i} x1={screwX + 4} y1={yPos} x2={screwX + screwW - 4} y2={yPos} stroke={color} strokeWidth={1} opacity={0.7} />;
            })}

            {/* Outlet nozzle */}
            <rect x={cx - 7} y={outletY} width={14} height={outletH} rx={2} fill="#1E293B" stroke={color} strokeWidth={1.5} />

            {/* Capacitive sensor dots (right side) */}
            {sensorDots.map(({ key, active, unbound, cy }) => {
                // Compute hopper right edge at this cy
                const edgeRatio = Math.min(1, cy / hopperH);
                const edgeX = topX + topW * (1 - edgeRatio) + (botX + botW) * edgeRatio;
                return (
                    <g key={key}>
                        <line x1={edgeX} y1={cy} x2={sensorX - 3} y2={cy} stroke="#475569" strokeWidth={1} />
                        <circle
                            cx={sensorX} cy={cy} r={4}
                            fill={unbound ? '#1E293B' : active ? '#22C55E' : '#EF4444'}
                            stroke={unbound ? '#475569' : active ? '#16A34A' : '#DC2626'}
                            strokeWidth={1}
                        />
                    </g>
                );
            })}
        </svg>
    );
}

export default function HopperScrewNode(props) {
    const { data } = props;
    return (
        <BaseScadaNode {...props}>
            <HopperScrewSVG config={data?.config} width={data?.width || 60} height={data?.height || 90} liveValues={data?.liveValues || {}} />
            {data?.label && (
                <div style={{ textAlign: 'center', fontSize: 10, color: '#94A3B8', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {data.label}
                </div>
            )}
        </BaseScadaNode>
    );
}
